import {
  BiggerSubscriptionDetail,
  SubscriptionDetail,
} from "../interfaces/subscriptionDetail";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getSubscriptionDetailByID = async (
  subscriptionDetailID: number,
): Promise<SubscriptionDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/GetSubscriptionDetailByID/${subscriptionDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `Subscription Detail ID ${subscriptionDetailID} was not found.`,
      );
    }

    const errorText = await response.text();
    console.error("Subscription Detail Fetch Error:", errorText);
    throw new Error("Failed to fetch subscription detail");
  }

  const subscriptionDetail: SubscriptionDetail = await response.json();
  return subscriptionDetail;
};

export const getSubscriptionDetailsBySubscriptionOrderID = async (
  subscriptionOrderID: number,
): Promise<SubscriptionDetail[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/GetSubscriptionDetailsBySubscriptionOrderID/${subscriptionOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Subscription Details Fetch Error:", errorText);
    throw new Error("Failed to fetch subscription details");
  }

  const subscriptionDetails: SubscriptionDetail[] = await response.json();
  return subscriptionDetails;
};

export const getActiveSubscriptionDetailsByClient = async (
  clientID: number,
): Promise<BiggerSubscriptionDetail[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/GetActiveSubscriptionDetailsByClient/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Subscription Details Fetch Error:", errorText);
    throw new Error("Failed to fetch subscription details");
  }

  const subscriptionDetails: BiggerSubscriptionDetail[] = await response.json();
  return subscriptionDetails;
};

export const getActiveSubscriptionDetails = async (): Promise<
  BiggerSubscriptionDetail[]
> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/GetActiveSubscriptionDetails`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Subscription Details Fetch Error:", errorText);
    throw new Error("Failed to fetch subscription details");
  }

  const subscriptionDetails: BiggerSubscriptionDetail[] = await response.json();
  return subscriptionDetails;
};

export const updateSubscriptionDetail = async (
  subscriptionDetail: SubscriptionDetail,
): Promise<SubscriptionDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/UpdateSubscriptionDetail`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(subscriptionDetail),
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Subscription Detail was not found to update.`);
    }

    const errorText = await response.text();
    console.error("Update Subscription Detail Error:", errorText);
    throw new Error("Failed to save subscription detail.");
  }

  const updatedDetail: SubscriptionDetail = await response.json();
  return updatedDetail;
};

export const stopSubscriptionDetail = async (
  subscriptionDetailID: number,
): Promise<SubscriptionDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/StopSubscriptionDetail/${subscriptionDetailID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Subscription Detail was not found to stop.`);
    }

    const errorText = await response.text();
    console.error("Stop Subscription Detail Error:", errorText);
    throw new Error("Failed to stop subscription detail.");
  }

  const updatedDetail: SubscriptionDetail = await response.json();
  return updatedDetail;
};

export const createSubscriptionDetail = async (
  subscriptionDetailCreate: SubscriptionDetail,
): Promise<SubscriptionDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/CreateSubscriptionDetail`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(subscriptionDetailCreate),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Subscription Detail Error:", errorText);
    throw new Error("Failed to create subscription detail.");
  }

  const subscriptionDetail: SubscriptionDetail = await response.json();
  return subscriptionDetail;
};

export const deleteSubscriptionDetail = async (
  subscriptionDetailID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/SubscriptionDetail/DeleteSubscriptionDetail/${subscriptionDetailID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `Subscription Detail ID ${subscriptionDetailID} was not found.`,
      );
    }

    const errorText = await response.text();
    console.error("Delete Subscription Detail Error:", errorText);
    throw new Error(
      `Failed to delete subscription detail with ID ${subscriptionDetailID}.`,
    );
  }
};
